import {Stack, Typography, TextField, Button, FormControlLabel, Checkbox, IconButton, Chip, useMediaQuery} from "@mui/material";
import {useState, useCallback, FormEvent, useRef, useContext, useEffect} from "react";
import {useSearchParams} from "react-router-dom";
import {VerifiedIcon, CheckedIcon, AttachIcon} from "../icons";
import {LoginFooter} from "./footer";
import axios from "axios";
import {AuthContext} from "../context/auth.context";
import mime from "mime";
import {base64ToFile, WYSIWYGEditor, FilesProvider} from "../context/files";
import {Add, Delete} from "@mui/icons-material";
import {DialogContext, DialogType} from "avrora";
import {validateEmail} from "../utils/email";

interface FormState {
    to: string[];
    copyTo: string[];
    theme: string;
    text: string;
    confirm: boolean;
    self: boolean;
    savedFiles: Array<{ name: string; data: string }>;
}


const maxMB = 20;
const maxFiles = 5;

const STORAGE_KEY = 'emailFormData';

const fileToBase64 = (file: File): Promise<{ name: string; data: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                const base64String = reader.result.split(',')[1];
                resolve({
                    name: file.name,
                    data: base64String
                });
            } else {
                reject(new Error('Failed to convert file to base64'));
            }
        };
        reader.onerror = error => reject(error);
    });
};

const base64ToFileObj = async (obj: { name: string; data: string }): Promise<File> => {
    const file = await base64ToFile(`data:${mime.getType(obj.name)};base64,` + obj.data, obj.name);
    return file;
};

export const Create = ({email}: { email?: string }) => {
    const [URLSearchParams, setURLSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const isMobile = useMediaQuery('(max-width:500px)');

    useEffect(() => {
        function sure(e: any) {
            e = e || window.event;
            if (e) {
                e.returnValue = 'Sure?';
            }
            return 'Sure?';
        }

        window.addEventListener('beforeunload', sure);
        return () => {
            window.removeEventListener('beforeunload', sure);
        }
    }, []);

    const {user} = useContext(AuthContext);

    const getInitialFormState = async (): Promise<{ formState: FormState; initialFiles: File[] }> => {
        const params = Object.fromEntries(URLSearchParams);
        const savedData = localStorage.getItem(STORAGE_KEY);

        let initialFiles: File[] = [];
        let formState: FormState = {
            to: params?.to?.split(',').map(i => i.trim()) ?? [''],
            copyTo: params?.copyTo?.split(',').map(i => i.trim()) ?? [],
            theme: window.emailSubject ? `Re: ${window.emailSubject}` : '',
            text: window.emailText ? `<br /><hr />${window.emailText}` : '',
            confirm: true,
            self: true,
            savedFiles: []
        };

        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                formState = {
                    ...formState,
                    to: params?.to?.split(',').map(i => i.trim()) ?? parsedData.to ?? formState.to,
                    copyTo: params?.copyTo?.split(',').map(i => i.trim()) ?? parsedData.copyTo ?? formState.copyTo,
                    theme: window.emailSubject ? `Re: ${window.emailSubject}` : parsedData.theme ?? formState.theme,
                    text: window.emailText ? `<br /><hr />${window.emailText}` : parsedData.text ?? formState.text,
                    confirm: parsedData.confirm ?? formState.confirm,
                    self: parsedData.self ?? formState.self,
                    savedFiles: parsedData.savedFiles ?? formState.savedFiles
                };

                if (parsedData.savedFiles) {
                    initialFiles = await Promise.all(parsedData.savedFiles.map(base64ToFileObj));
                }
            } catch (e) {
                console.error('Failed to parse saved data', e);
            }
        }

        if (window.files) {
            const windowFiles = window.files.map((i: any) =>
                base64ToFile(`data:${mime.getType(i.filename)};base64,` + i.file, i.filename));
            initialFiles = [...initialFiles, ...windowFiles];
        }

        return {formState, initialFiles};
    };

    const [formState, setFormState] = useState<FormState>({
        to: [''],
        copyTo: [],
        theme: '',
        text: '',
        confirm: true,
        self: true,
        savedFiles: []
    });
    const [files, setFiles] = useState<File[]>([]);

    const [sent, setSent] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const dialogContext = useContext(DialogContext);

    useEffect(() => {
        const initialize = async () => {
            const {formState, initialFiles} = await getInitialFormState();
            setFormState(formState);
            setFiles(initialFiles);
            setIsInitialized(true);
        };

        initialize();
    }, []);

    useEffect(() => {
        if (!isInitialized) return;

        const saveData = async () => {
            const filesToSave = await Promise.all(files.map(fileToBase64));
            const dataToSave = {
                ...formState,
                savedFiles: filesToSave
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        };

        saveData();
    }, [formState, files, isInitialized]);
    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();
        const formDataToSend = new FormData();

        const body: Record<string, any> = {
            subject: formState.theme,
            to: [...formState.to.filter(Boolean)],
            copyTo: [...formState.copyTo.filter(Boolean), ...(formState.self ? [user.sub] : [])],
            text: formState.text
        };

        Object.keys(body).forEach(key => formDataToSend.append(key, body[key]));

        files?.forEach(file => {
            formDataToSend.append('files', file);
        });

        setLoading(true);

        axios.post(`/eos/encrypt`, formDataToSend).then(resp => {
            const results = resp.data?.results;

            if (!Array.isArray(results) || results.length === 0) {
                return Promise.reject('Сервер вернул некорректный ответ.');
            }

            const expectedRecipients = [
                ...formState.to.filter(Boolean),
                ...formState.copyTo.filter(Boolean),
                ...(formState.self ? [user.sub] : [])
            ].filter(email => validateEmail(email));

            const normalizedExpected = expectedRecipients.map(email => email.toLowerCase());
            const uniqueExpected = Array.from(new Set(normalizedExpected));

            const processedAddresses = new Set(results.map((r: any) => r.address.toLowerCase()));

            const missingAddresses = uniqueExpected.filter(addr => !processedAddresses.has(addr));

            const errorRecipientsFromResponse = results
              .filter((item: any) => item.status === "error")
              .map((item: any) => item.address.toLowerCase());

            const allErrorRecipients = [...new Set([...errorRecipientsFromResponse, ...missingAddresses])];

            const successRecipients = results
              .filter((item: any) => item.status === "sent" && item.address.toLowerCase() !== user.sub.toLowerCase())
              .map((item: any) => item.address)
              .filter(addr => !allErrorRecipients.includes(addr.toLowerCase()));

            if (allErrorRecipients.length > 0) {
                const errorList = allErrorRecipients.join(', ');
                const successPart = successRecipients.length > 0
                  ? `Письмо успешно отправлено на: ${successRecipients.join(', ')}. `
                  : '';

                return Promise.reject(
                  `${successPart}Возникла ошибка, не удалось отправить письмо по адресу: ${errorList}.`
                );
            }

            setSent(true);
            setURLSearchParams();
            localStorage.removeItem(STORAGE_KEY);
        }).catch(e => {
            dialogContext.push({
                text: typeof e === 'string' ? e : (e?.response?.data?.message ? JSON.stringify(e.response.data.message) : 'Возникла неизвестная ошибка, обратитесь в поддержку.'),
                title: "Ошибка",
                type: DialogType.Error
            });
        }).finally(() => {
            setLoading(false);
        });
    }, [formState, user.sub, files, setURLSearchParams, dialogContext]);

    const reload = useCallback(() => {
        window.files = undefined;
        window.emailSubject = undefined;
        window.emailText = undefined;
        localStorage.removeItem(STORAGE_KEY);

        setFormState({
            to: [''],
            copyTo: [],
            theme: '',
            text: '',
            confirm: true,
            self: true,
            savedFiles: []
        });
        setFiles([]);
        setSent(false);
        setURLSearchParams();
    }, [setURLSearchParams]);

    if (sent) {
        return (
            <Stack maxWidth={isMobile ? "100%" : "816px"} padding={isMobile ? "1rem" : "3rem"} spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
                <Typography variant="h1">Письмо отправлено</Typography>
                <Typography>Можно закрыть страницу или написать еще одно письмо</Typography>
                <Stack alignItems="center">
                    <Button size="large" onClick={reload}>Написать новое письмо</Button>
                </Stack>
                <LoginFooter/>
            </Stack>
        )
    }

    return (
        <form onSubmit={handleSubmit}>
            <Stack maxWidth={isMobile ? "100%" : "816px"} padding={isMobile ? "1rem" : "3rem"} spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
                <Typography
                    variant="h1">{Object.fromEntries(URLSearchParams)?.to ? "Ответ" : "Создать сообщение"}</Typography>

                <Stack spacing={2}>
                    <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
                        <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"}>
                            <Typography>От кого:</Typography>
                        </Stack>

                        <Stack flexGrow={1} width={isMobile ? "100%" : "auto"}>
                            <Typography sx={{ wordBreak: "break-word" }}>{user.sub} (подлинность проверена)</Typography>
                        </Stack>
                    </Stack>

                    <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
                        <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"}>

                        </Stack>

                        <Stack flexGrow={1} width={isMobile ? "100%" : "auto"}>
                            <Stack direction={isMobile ? "column" : "row"} spacing={2} flexWrap="wrap">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <VerifiedIcon/>
                                    <Typography>Подлинность проверена</Typography>
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CheckedIcon/>
                                    <Typography>Верная подпись</Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>


                </Stack>

                <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 0}>
                    <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"} pt={isMobile ? 0 : 1}>
                        <Typography>Кому:</Typography>
                    </Stack>

                    <Stack flexGrow={1} spacing={2} width={isMobile ? "100%" : "auto"}>
                        {formState.to.map((i, j) => (
                            <Stack key={j} spacing={1} alignItems="center" direction="row" width="100%">
                                <Stack flexGrow={1} minWidth={0}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                autoComplete: 'new-password',
                                            }
                                        }}
                                        error={!!i && !validateEmail(i)}
                                        value={i}
                                        onChange={e => {
                                            const val = e.target.value.replace(/[:!?<>&\s]/g, '');
                                            const items = val.trim().split(/[,;]/);

                                            setFormState(old => {
                                                return {
                                                    ...old,
                                                    to: [...old.to.slice(0, j), ...items, ...old.to.slice(j + 1)]
                                                }
                                            })
                                        }}
                                        autoFocus
                                    />
                                </Stack>

                                {formState.to.length > 1 && (
                                    <Stack flexShrink={0}>
                                        <IconButton size="small" onClick={() => setFormState(old => ({
                                            ...old,
                                            to: old.to.filter((ii, jj) => jj !== j)
                                        }))}><Delete/></IconButton>
                                    </Stack>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Stack>


                <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
                    <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"}>

                    </Stack>

                    <Stack direction="row">
                        <Button variant="text" onClick={() => setFormState(old => ({...old, to: [...old.to, '']}))}
                                startIcon={<Add/>}>Добавить адрес</Button>
                    </Stack>
                </Stack>

                <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 0}>
                    <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"} pt={isMobile ? 0 : 1}>
                        <Typography>Копия:</Typography>
                    </Stack>

                    <Stack flexGrow={1} spacing={2} width={isMobile ? "100%" : "auto"}>
                        {formState.copyTo.map((i, j) => (
                            <Stack key={j} spacing={1} alignItems="center" direction="row" width="100%">
                                <Stack flexGrow={1} minWidth={0}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                autoComplete: 'new-password',
                                            }
                                        }}
                                        value={i}
                                        error={!!i && !validateEmail(i)}
                                        onChange={e => {
                                            const val = e.target.value.replace(/[:!?<>&\s]/g, '');
                                            const items = val.trim().split(/[,;]/);

                                            setFormState(old => {
                                                return {
                                                    ...old,
                                                    copyTo: [...old.copyTo.slice(0, j), ...items, ...old.copyTo.slice(j + 1)]
                                                }
                                            })
                                        }}
                                        autoFocus
                                    />
                                </Stack>

                                <Stack flexShrink={0}>
                                    <IconButton size="small" onClick={() => setFormState(old => ({
                                        ...old,
                                        copyTo: old.copyTo.filter((ii, jj) => jj !== j)
                                    }))}><Delete/></IconButton>
                                </Stack>
                            </Stack>
                        ))}

                        <Stack direction={isMobile ? "column" : "row"}>
                            <Button variant="text"
                                    onClick={() => setFormState(old => ({...old, copyTo: [...old.copyTo, '']}))}
                                    startIcon={<Add/>}>Добавить копию</Button>
                        </Stack>
                    </Stack>
                </Stack>

                <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
                    <Stack width={isMobile ? "100%" : "90px"} minWidth={isMobile ? "auto" : "90px"}>
                        <Typography>Тема:</Typography>
                    </Stack>

                    <Stack flexGrow={1} width={isMobile ? "100%" : "auto"}>
                        <TextField
                            size="small"
                            fullWidth
                            slotProps={{
                                input: {
                                    autoComplete: 'new-password',
                                }
                            }}
                            value={formState.theme}
                            onChange={e => setFormState(old => ({...old, theme: e.target.value}))}
                            autoFocus
                        />
                    </Stack>
                </Stack>

                <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
                    {!isMobile && <Stack minWidth="90px"></Stack>}

                    <Stack flexGrow={1} spacing={2} width={isMobile ? "100%" : "auto"}>
                        {!!files?.length && (
                            <Stack gap={1} direction="row" flexWrap="wrap">
                                {files?.map((i, j) => (
                                    <Chip variant="outlined" size="small" key={j} label={i.name}
                                          onDelete={() => setFiles(old => old?.filter(k => k !== i) ?? [])}/>
                                ))}

                                <Typography
                                    variant="body2" sx={{ wordBreak: "break-word" }}>{Math.ceil((files.reduce((coll, i) => coll + i.size, 0) * 10) / (1024 * 1024)) / 10} из {maxMB} MB
                                    занято. {files.length} из {maxFiles} файлов.</Typography>
                            </Stack>
                        )}
                        <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 0}>
                            <Stack ml={-0.5}>
                                <div style={{visibility: "hidden", width: 0, height: 0}}>
                                    <input multiple type="file" style={{}} ref={fileRef} onChange={async e => {
                                        const openedFiles = Array.from(e.target.files ?? []) as File[];

                                        const currentFilesCount = files.length;
                                        const currentFilesSize = files.reduce((coll, i) => coll + i.size, 0);

                                        const openedFilesCount = openedFiles.length;
                                        const openedFilesSize = openedFiles.reduce((coll, i) => coll + i.size, 0);

                                        e.target.value = '';

                                        if (openedFilesCount + currentFilesCount > maxFiles) {
                                            return dialogContext.push({
                                                type: DialogType.Error,
                                                title: "Ошибка",
                                                text: "Общее количество файлов не должно превышать " + maxFiles
                                            });
                                        }

                                        if (openedFilesSize + currentFilesSize > maxMB * 1024 * 1024) {
                                            return dialogContext.push({
                                                type: DialogType.Error,
                                                title: "Ошибка",
                                                text: "Общий размер файлов не должен превышать " + maxMB + " MB"
                                            });
                                        }

                                        setFiles(old => [...old, ...openedFiles].slice(0, 100));
                                    }}/>
                                </div>

                                {(!files || files.length < 100) && (
                                    <Button variant="text" onClick={() => fileRef.current?.click()}
                                            startIcon={<AttachIcon/>}>Прикрепить файл</Button>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>

                <Stack>
                    {isInitialized && files && formState.text !== undefined && (
                        <FilesProvider
                            value={formState.text}
                            files={files}
                            onChange={async (text, files) => {
                                setFormState(old => ({...old, text}));
                                setFiles(files);
                            }}
                        >
                            <WYSIWYGEditor/>
                        </FilesProvider>
                    )}
                </Stack>

                <Stack>
                    <Stack ml={-1.25} spacing={2}>
                        <FormControlLabel
                            control={<Checkbox/>}
                            checked={formState.self}
                            onChange={() => setFormState(old => ({...old, self: !old.self}))}
                            label="Отправить копию себе"
                        />
                    </Stack>
                </Stack>

                <Stack alignItems="center">
                    <Button size="large" type="submit"
                            disabled={loading || !formState.to.find(Boolean) || !!formState.to.find(i => !!i && !validateEmail(i)) || !!formState.copyTo.find(i => !!i && !validateEmail(i)) || !formState.theme || !formState.text}>Отправить
                        защищенное письмо</Button>
                </Stack>

                <LoginFooter/>
            </Stack>
        </form>
    )
}
